import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'
import Element, { RootEntityType } from '@/modules/elements/models/Element'
import { useElementStore } from '@/modules/elements/stores/ElementStore'
import { useConfirmModalStore } from '@/modules/global/stores/ConfirmModalStore'
import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { extractApiErrorCode } from '@/util/api-error'
import i18n from '@/util/config/i18n'

const DOCUMENT_SIGNED_ERROR_CODE = 'document-signed'
const VARIABLE_SCHEMA_UNLOCK_CONFIRM_KIND = 'variable-schema-unlock'

const unlockInFlight = new Map<string, Promise<boolean>>()
const unlockInProgressSnackbarShown = new Set<string>()

export type VariableSchemaUnlockOptions = {
  /** Re-open the interrupted UI after successful unlock */
  resume?: () => void | Promise<void>
  /** Restore UI if unlock is cancelled or fails */
  onCancel?: () => void
}

type PendingUnlockIntent = {
  spaceId: string
  resume?: () => void | Promise<void>
  onCancel?: () => void
}

const pendingUnlockIntents: PendingUnlockIntent[] = []

/** True when BE refused a variable schema mutation because the document is signed. */
export function isDocumentSignedError(error: unknown): boolean {
  return extractApiErrorCode(error) === DOCUMENT_SIGNED_ERROR_CODE
}

function registerUnlockIntent(spaceId: string, options?: VariableSchemaUnlockOptions): void {
  if (!options?.resume && !options?.onCancel) {
    return
  }
  pendingUnlockIntents.push({
    spaceId,
    resume: options.resume,
    onCancel: options.onCancel
  })
}

function clearUnlockIntent(): void {
  pendingUnlockIntents.length = 0
}

function runCancelCallbacks(intents: PendingUnlockIntent[]): void {
  for (const intent of intents) {
    if (!intent.onCancel) {
      continue
    }
    try {
      intent.onCancel()
    } catch (error) {
      console.error('Failed to run variable schema unlock cancel callback:', error)
    }
  }
}

function cancelUnlockIntent(): void {
  const intents = [...pendingUnlockIntents]
  clearUnlockIntent()
  runCancelCallbacks(intents)
}

/** Clears pending unlock intent and dismisses the unlock confirm when the caller closes without using Cancel. */
export function cancelVariableSchemaUnlockFlow(): void {
  cancelUnlockIntent()
  const confirmStore = useConfirmModalStore()
  const modal = confirmStore.modals.find(m => m.kind === VARIABLE_SCHEMA_UNLOCK_CONFIRM_KIND)
  if (modal) {
    confirmStore.remove(modal.id)
  }
}

function extractIntentsForSpace(unlockedSpaceId: string): PendingUnlockIntent[] {
  const matching: PendingUnlockIntent[] = []
  for (let index = pendingUnlockIntents.length - 1; index >= 0; index -= 1) {
    if (pendingUnlockIntents[index].spaceId === unlockedSpaceId) {
      matching.unshift(pendingUnlockIntents[index])
      pendingUnlockIntents.splice(index, 1)
    }
  }
  return matching
}

async function runUnlockIntentResume(unlockedSpaceId: string): Promise<void> {
  const intents = extractIntentsForSpace(unlockedSpaceId)
  if (intents.length === 0) {
    return
  }

  const currentSpaceId = useSpaceStore().currentSpaceId
  if (!currentSpaceId || currentSpaceId !== unlockedSpaceId) {
    runCancelCallbacks(intents)
    return
  }

  let hadError = false
  for (const intent of intents) {
    if (!intent.resume) {
      continue
    }
    try {
      await intent.resume()
    } catch (error) {
      hadError = true
      console.error('Failed to resume after variable schema unlock:', error)
    }
  }

  if (hadError) {
    showUnlockErrorSnackbar()
  }
}

function currentSpacePageIds(): Set<string> {
  const spaceStore = useSpaceStore()
  const spaceId = spaceStore.currentSpaceId
  if (!spaceId) {
    return new Set()
  }

  const pageIds = new Set((spaceStore.spacePages[spaceId] || []).map(page => page.id))
  if (pageIds.size === 0 && spaceStore.currentPageId) {
    pageIds.add(spaceStore.currentPageId)
  }
  return pageIds
}

/** Elements loaded for pages that belong to the currently open space only. */
function currentSpaceScopedElements(): Element[] {
  const pageIds = currentSpacePageIds()
  if (pageIds.size === 0) {
    return []
  }

  return useElementStore().elements.filter(
    element =>
      element.rootEntityType === RootEntityType.SPACE_PAGE &&
      !!element.rootEntityId &&
      pageIds.has(element.rootEntityId)
  )
}

function currentSpaceSignaturePositions() {
  const spaceStore = useSpaceStore()
  const spaceId = spaceStore.currentSpaceId
  if (!spaceId || spaceStore.signaturePositionsSpaceId !== spaceId) {
    return []
  }
  return spaceStore.signaturePositions
}

/**
 * Space-wide signed check. Prefers `signaturePositions` (loaded for the whole space) over
 * ElementStore elements, which usually only contain the current page.
 */
export function isVariableSchemaLocked(): boolean {
  if (currentSpaceSignaturePositions().some(position => position.signed)) {
    return true
  }
  return usePermissionStore().isSpaceSigned(currentSpaceScopedElements())
}

function signedPageIdsForUnlock(): string[] {
  const fromPositions = [
    ...new Set(
      currentSpaceSignaturePositions()
        .filter(position => position.signed)
        .map(position => position.pageId)
        .filter(Boolean)
    )
  ]
  if (fromPositions.length > 0) {
    return fromPositions
  }
  return usePermissionStore().signedPageIds(currentSpaceScopedElements())
}

function showUnlockErrorSnackbar(): void {
  useSnackbarStore().enqueue({
    variant: 'alert',
    leadingIcon: true,
    label: i18n.t('variableModal.messages.error') as string
  })
}

function showUnlockSuccessSnackbar(): void {
  useSnackbarStore().enqueue({
    variant: 'success',
    leadingIcon: true,
    label: i18n.t('variableModal.unlock.success') as string
  })
}

function showUnlockInProgressSnackbar(spaceId: string): void {
  if (unlockInProgressSnackbarShown.has(spaceId)) {
    return
  }
  unlockInProgressSnackbarShown.add(spaceId)
  useSnackbarStore().enqueue({
    variant: 'default',
    leadingIcon: true,
    label: i18n.t('variableModal.unlock.inProgress') as string
  })
}

async function refreshSpaceAfterUnlock(spaceId: string): Promise<boolean> {
  const spaceStore = useSpaceStore()
  try {
    await Promise.all([spaceStore.fetchSpace(spaceId), spaceStore.getSignaturePositions(spaceId)])
    return true
  } catch {
    return false
  }
}

/**
 * Clears signatures on every signed page. Refreshes space + positions once at the end so a per-page
 * positions failure cannot abort the loop. Returns true when the schema is no longer locked.
 */
export async function executeVariableSchemaUnlock(spaceId: string): Promise<boolean> {
  const inFlight = unlockInFlight.get(spaceId)
  if (inFlight) {
    return inFlight
  }

  const promise = (async (): Promise<boolean> => {
    const spaceStore = useSpaceStore()
    let anyClearFailed = false

    try {
      let ids = signedPageIdsForUnlock()
      if (ids.length === 0) {
        try {
          await spaceStore.getSignaturePositions(spaceId)
        } catch {
          // Best effort — fall through to element-store ids.
        }
        ids = signedPageIdsForUnlock()
      }

      if (ids.length === 0) {
        if (isVariableSchemaLocked()) {
          showUnlockErrorSnackbar()
          return false
        }
        return true
      }

      const clearResults = await Promise.allSettled(
        ids.map(pageId => spaceStore.clearSignaturesOfPage(spaceId, pageId, { refreshPositions: false }))
      )
      anyClearFailed = clearResults.some(result => result.status === 'rejected')

      const refreshed = await refreshSpaceAfterUnlock(spaceId)
      if (!refreshed) {
        showUnlockErrorSnackbar()
        return false
      }

      if (anyClearFailed || isVariableSchemaLocked()) {
        showUnlockErrorSnackbar()
        return false
      }

      return true
    } catch {
      showUnlockErrorSnackbar()
      return false
    } finally {
      unlockInFlight.delete(spaceId)
      unlockInProgressSnackbarShown.delete(spaceId)
    }
  })()

  unlockInFlight.set(spaceId, promise)
  return promise
}

/** Runs after NConfirmModalProvider tears down the confirm backdrop (macrotask, not microtask). */
function deferAfterConfirmClose(callback: () => void): void {
  setTimeout(callback, 0)
}

async function handleUnlockConfirm(expectedSpaceId: string): Promise<void> {
  const spaceId = useSpaceStore().currentSpaceId
  if (!spaceId || spaceId !== expectedSpaceId) {
    cancelUnlockIntent()
    return
  }

  const success = await executeVariableSchemaUnlock(spaceId)
  if (success) {
    showUnlockSuccessSnackbar()
    deferAfterConfirmClose(() => {
      void runUnlockIntentResume(spaceId)
    })
  } else {
    deferAfterConfirmClose(() => {
      cancelUnlockIntent()
    })
  }
}

/**
 * Opens the unlock confirm modal so the user knows why variable schema is frozen and can
 * clear signatures on every signed page. Returns true when the modal was shown (or already open).
 */
export function showVariableSchemaUnlockConfirm(options?: VariableSchemaUnlockOptions): boolean {
  const spaceStore = useSpaceStore()
  const spaceId = spaceStore.currentSpaceId
  if (!spaceId) {
    return false
  }

  registerUnlockIntent(spaceId, options)

  if (unlockInFlight.has(spaceId)) {
    showUnlockInProgressSnackbar(spaceId)
    return true
  }

  const confirmStore = useConfirmModalStore()
  const existingModal = confirmStore.modals.find(modal => modal.kind === VARIABLE_SCHEMA_UNLOCK_CONFIRM_KIND)
  if (existingModal) {
    return true
  }

  const spaceName = spaceStore.currentSpace?.name || (i18n.t('spacelist.untitledSpace') as string)
  const confirmButtonText = i18n.t('variableModal.unlock.confirm') as string

  confirmStore.showConfirm({
    kind: VARIABLE_SCHEMA_UNLOCK_CONFIRM_KIND,
    title: i18n.t('variableModal.unlock.title', { name: spaceName }),
    message: i18n.t('variableModal.unlock.description', { name: spaceName }),
    confirmButtonText,
    onConfirm: async () => {
      await handleUnlockConfirm(spaceId)
    },
    onCancel: () => {
      cancelUnlockIntent()
    }
  })

  return true
}

/**
 * If the space has any completed signature, variable schema create/modify/delete/assignment
 * is frozen (BE assertSpaceNotSigned). Opens the unlock confirm modal so the user knows why
 * and can clear signatures on every signed page. Returns true when the action was blocked.
 */
export function promptUnlockIfVariableSchemaLocked(options?: VariableSchemaUnlockOptions): boolean {
  if (!isVariableSchemaLocked()) {
    return false
  }
  return showVariableSchemaUnlockConfirm(options)
}

/**
 * When BE rejects a space-scoped variable mutation with `document-signed`, opens the unlock
 * confirm instead of surfacing a generic error. Returns true when handled.
 */
export function handleDocumentSignedVariableSchemaError(
  error: unknown,
  isSpaceScope = true,
  options?: VariableSchemaUnlockOptions
): boolean {
  if (!isSpaceScope || !isDocumentSignedError(error)) {
    return false
  }
  showVariableSchemaUnlockConfirm(options)
  return true
}

export type VariableDeleteApiErrorOptions = {
  isSpaceScope: boolean
  onDocumentSigned: () => void
  onRecover: () => void
}

/** Routes variable-delete API failures to unlock flow or menu recovery. */
export function handleVariableDeleteApiError(error: unknown, options: VariableDeleteApiErrorOptions): void {
  if (
    handleDocumentSignedVariableSchemaError(error, options.isSpaceScope, {
      resume: options.onRecover
    })
  ) {
    options.onDocumentSigned()
    return
  }
  options.onRecover()
}
